import { useState, useRef, useEffect } from "react";
import { MapPin, Loader2, Navigation } from "lucide-react";

// Free UK address finder — no API key.
//  • Postcodes: validated town/county/coords via postcodes.io (Ordnance Survey open data)
//  • Street search: OpenStreetMap Nominatim
const PC_PARTIAL = /^[A-Za-z]{1,2}\d[A-Za-z\d]?\s*\d?[A-Za-z]{0,2}$/;
const looksLikePostcode = (q) => {
  const t = (q || "").trim();
  return t.length >= 2 && t.length <= 8 && !t.includes(",") && PC_PARTIAL.test(t);
};

export function AddressAutocomplete({ label = "Address", value, onChange, onSelect, required, placeholder = "Type a postcode or street…", testid = "address-autocomplete" }) {
  const [results, setResults] = useState([]); // {kind:'postcode'|'address', ...}
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const timer = useRef(null);
  const boxRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const osmSearch = async (q) => {
    const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&countrycodes=gb&addressdetails=1&limit=6&q=${encodeURIComponent(q)}`;
    const res = await fetch(url, { headers: { "Accept-Language": "en-GB" } });
    const data = await res.json();
    return (Array.isArray(data) ? data : []).map((item) => ({ kind: "address", item }));
  };

  const search = (q) => {
    if (timer.current) clearTimeout(timer.current);
    const t = (q || "").trim();
    if (t.length < 2) { setResults([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        let list = [];
        if (looksLikePostcode(t)) {
          const pc = t.replace(/\s+/g, "");
          const res = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(pc)}/autocomplete`);
          const data = await res.json();
          list = ((data && data.result) || []).map((p) => ({ kind: "postcode", postcode: p }));
          if (list.length === 0 && t.length >= 3) list = await osmSearch(t); // fallback
        } else if (t.length >= 3) {
          list = await osmSearch(t);
        }
        setResults(list);
        setOpen(true);
        setActive(-1);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 320);
  };

  const parse = (item) => {
    const a = item.address || {};
    const line = [a.house_number, a.road || a.pedestrian || a.footway].filter(Boolean).join(" ")
      || (item.display_name || "").split(",")[0];
    const city = a.city || a.town || a.village || a.hamlet || a.suburb || a.city_district || a.county || "";
    const county = a.county || a.state_district || a.state || "";
    return { line, city, county, postcode: a.postcode || "" };
  };

  const chooseAddress = (item) => {
    const { line, city, county, postcode } = parse(item);
    onChange(line);
    onSelect && onSelect({ address: line, city, county, postcode, latitude: item.lat, longitude: item.lon, raw: item });
    setOpen(false); setResults([]);
  };

  const choosePostcode = async (pc) => {
    setLoading(true);
    try {
      const res = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(pc.replace(/\s+/g, ""))}`);
      const data = await res.json();
      const r = (data && data.result) || {};
      const city = r.admin_district || r.parish || r.region || "";
      const county = r.admin_county || r.region || r.country || "";
      onChange("");
      onSelect && onSelect({ address: "", city, county, postcode: r.postcode || pc, latitude: r.latitude, longitude: r.longitude, raw: r });
      setOpen(false); setResults([]);
      setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
    } catch {
      onSelect && onSelect({ address: "", city: "", county: "", postcode: pc });
      setOpen(false); setResults([]);
    } finally { setLoading(false); }
  };

  const choose = (r) => (r.kind === "postcode" ? choosePostcode(r.postcode) : chooseAddress(r.item));

  const onKey = (e) => {
    if (!open || !results.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" && active >= 0) { e.preventDefault(); choose(results[active]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div className="relative" ref={boxRef}>
      <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
      <div className="relative">
        <MapPin size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          data-testid={testid}
          ref={inputRef}
          value={value}
          required={required}
          placeholder={placeholder}
          autoComplete="off"
          onChange={(e) => { onChange(e.target.value); search(e.target.value); }}
          onFocus={() => { if (results.length) setOpen(true); }}
          onKeyDown={onKey}
          className="w-full rounded-md border border-slate-300 pl-9 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
        {loading && <Loader2 size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />}
      </div>
      {open && results.length > 0 && (
        <ul data-testid="address-suggestions" className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {results.map((r, i) => (
            <li key={r.kind === "postcode" ? r.postcode : r.item.place_id}>
              <button type="button" data-testid={`address-option-${i}`} onMouseDown={(e) => { e.preventDefault(); choose(r); }}
                className={`w-full text-left px-3 py-2 text-sm flex items-start gap-2 ${i === active ? "bg-blue-50" : "hover:bg-slate-50"}`}>
                {r.kind === "postcode" ? (
                  <><Navigation size={13} className="text-emerald-600 mt-0.5 shrink-0" />
                    <span className="text-slate-700 leading-snug font-medium">{r.postcode} <span className="text-slate-400 font-normal">· fills town & county</span></span></>
                ) : (
                  <><MapPin size={13} className="text-blue-600 mt-0.5 shrink-0" />
                    <span className="text-slate-700 leading-snug">{r.item.display_name}</span></>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default AddressAutocomplete;
